import React, { useEffect, useState } from "react";
import { Download, Share, Smartphone, PlusSquare, MoreVertical, CheckCircle2 } from "lucide-react";

const getPlatform = () => {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "other";
};

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

export const InstallAppButton = ({ variant = "solid" }) => {
  const [deferred, setDeferred] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [installed, setInstalled] = useState(false);
  const platform = getPlatform();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      setShowModal(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setShowModal(true);
    }
  };

  if (isStandalone() || installed) {
    return (
      <span
        data-testid="install-app-done"
        className="inline-flex items-center gap-2 font-heading font-600 text-sm text-candy-teal"
      >
        <CheckCircle2 className="h-5 w-5" /> App installed — find it on your home screen!
      </span>
    );
  }

  const base =
    "font-bold uppercase tracking-wider py-3 px-6 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 flex items-center justify-center gap-2";
  const style = variant === "solid" ? "bg-candy-teal text-black" : "bg-candy-card text-candy-cream";

  return (
    <>
      {variant === "link" ? (
        <button
          data-testid="install-app-link"
          onClick={handleInstall}
          className="inline-flex items-center gap-2 font-heading font-600 text-candy-teal underline decoration-2 underline-offset-4 hover:text-candy-pink transition-colors"
        >
          <Smartphone className="h-5 w-5" /> Get the app on your phone
        </button>
      ) : (
        <button data-testid="install-app-btn" onClick={handleInstall} className={`${base} ${style}`}>
          <Download className="h-5 w-5" /> Get the App
        </button>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
          data-testid="install-modal"
        >
          <div
            className="spooky-card border-4 border-candy-pink rounded-2xl p-6 max-w-sm w-full shadow-brutal text-candy-cream"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Smartphone className="h-6 w-6 text-candy-teal" />
              <h3 className="font-display text-3xl text-candy-cream leading-none">Add our app</h3>
            </div>

            {platform === "ios" && (
              <ol className="mt-4 space-y-3 text-sm font-medium">
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">1.</span>
                  <span>
                    Tap the <Share className="inline h-4 w-4 -mt-0.5" /> <b>Share</b> button at the bottom of Safari.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">2.</span>
                  <span>
                    Scroll down and tap <PlusSquare className="inline h-4 w-4 -mt-0.5" /> <b>&ldquo;Add to Home Screen.&rdquo;</b>
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">3.</span>
                  <span>Tap <b>Add</b> — our icon lands on your home screen. Done! 🎄</span>
                </li>
              </ol>
            )}

            {platform === "android" && (
              <ol className="mt-4 space-y-3 text-sm font-medium">
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">1.</span>
                  <span>
                    Tap the <MoreVertical className="inline h-4 w-4 -mt-0.5" /> menu (top-right of Chrome).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">2.</span>
                  <span>
                    Tap <b>&ldquo;Install app&rdquo;</b> (or &ldquo;Add to Home screen&rdquo;).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-candy-pink">3.</span>
                  <span>Confirm <b>Install</b> — our icon lands on your home screen. Done! 🎄</span>
                </li>
              </ol>
            )}

            {platform === "other" && (
              <div className="mt-4 space-y-3 text-sm font-medium">
                <p><b>On your phone,</b> open this site in your browser, then:</p>
                <p>
                  <b>iPhone (Safari):</b> tap <Share className="inline h-4 w-4 -mt-0.5" /> Share →{" "}
                  <b>&ldquo;Add to Home Screen.&rdquo;</b>
                </p>
                <p>
                  <b>Android (Chrome):</b> tap the <MoreVertical className="inline h-4 w-4 -mt-0.5" /> menu →{" "}
                  <b>&ldquo;Install app.&rdquo;</b>
                </p>
              </div>
            )}

            <button
              data-testid="install-modal-close"
              onClick={() => setShowModal(false)}
              className="mt-6 w-full bg-candy-pink text-white font-bold uppercase py-2.5 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
